// /!\ Vibe coded
// Permet d'ajouter un tooltip sur les éléments dont le texte déborde (text-overflow: ellipsis).

import type { Directive } from "vue";
// floating-vue est une dépendance transitive de @lde/core_lde_vue
import { createTooltip, destroyTooltip } from "floating-vue";

/**
 * Interface étendant HTMLElement pour stocker les observers et le tooltip sur l'élément.
 */
interface TitleOverflowElement extends HTMLElement {
  _titleOverflowResizeObserver?: ResizeObserver;
  _titleOverflowMutationObserver?: MutationObserver;
  _titleOverflowTooltip?: ReturnType<typeof createTooltip>;
}

/**
 * Met à jour le tooltip d'un élément si son contenu déborde (ellipsis).
 * Compare scrollWidth à clientWidth pour détecter le débordement horizontal.
 * Utilise floating-vue (v-tooltip) pour afficher le tooltip au survol.
 * @param el L'élément HTML à vérifier.
 */
function updateTooltip(el: TitleOverflowElement): void {
  const isOverflowing = el.scrollWidth > el.clientWidth;
  const content = el.textContent?.trim() || "";

  if (isOverflowing && content) {
    if (el._titleOverflowTooltip) {
      // Mettre à jour le contenu si le tooltip existe déjà
      destroyTooltip(el);
    }

    // Je garde ça au cas où, mais ça ne fonctionne pas s'il n'y a pas d'élément dans l'objet (genre que du texte)
    // Trouver le nœud texte ou élément qui contient le texte tronqué pour attacher le tooltip
    // const element = Array.from(el.childNodes).find(
    //   (node) => (node.nodeType === Node.ELEMENT_NODE) && node.textContent?.trim() === content,
    // ) || el;

    el._titleOverflowTooltip = createTooltip(el, {
      content,
      placement: "top",
      delay: { show: 800, hide: 0 },
    }, {});
  } else if (el._titleOverflowTooltip) {
    destroyTooltip(el);
    el._titleOverflowTooltip = undefined;
  }
}

/**
 * Directive Vue 3 `v-title-overflow`.
 *
 * Ajoute automatiquement un tooltip (via floating-vue / v-tooltip) sur les
 * éléments dont le texte est tronqué par text-overflow: ellipsis, afin que
 * l'utilisateur puisse voir le texte complet au survol.
 *
 * Utilise un ResizeObserver pour détecter les changements de taille
 * et un MutationObserver pour détecter les changements de contenu.
 *
 * Usage : `<td v-title-overflow>{{ texte }}</td>`
 */
const titleOverflowDirective: Directive = {
  mounted(el: TitleOverflowElement) {
    // Vérification initiale (dans un requestAnimationFrame pour s'assurer que le layout est calculé)
    requestAnimationFrame(() => updateTooltip(el));

    // Observer les changements de taille de l'élément
    const resizeObserver = new ResizeObserver(() => {
      updateTooltip(el);
    });
    resizeObserver.observe(el);

    // Observer les changements de contenu textuel
    const mutationObserver = new MutationObserver(() => {
      // Attendre le prochain frame pour que le layout soit recalculé
      requestAnimationFrame(() => updateTooltip(el));
    });
    mutationObserver.observe(el, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    // Stocker les observers pour le nettoyage
    el._titleOverflowResizeObserver = resizeObserver;
    el._titleOverflowMutationObserver = mutationObserver;
  },

  updated(el: TitleOverflowElement) {
    // Revérifier à chaque mise à jour du composant
    requestAnimationFrame(() => updateTooltip(el));
  },

  unmounted(el: TitleOverflowElement) {
    // Nettoyer les observers et le tooltip
    el._titleOverflowResizeObserver?.disconnect();
    el._titleOverflowMutationObserver?.disconnect();

    if (el._titleOverflowTooltip) {
      destroyTooltip(el);
    }

    delete el._titleOverflowResizeObserver;
    delete el._titleOverflowMutationObserver;
    delete el._titleOverflowTooltip;
  },
};

export default titleOverflowDirective;
